/**
 * First-touch attribution helper.
 * Captures UTM params + referrer + landing page on the very first page load of a session,
 * stores them in sessionStorage, and makes them available for any POST to the API.
 *
 * All sessionStorage access is guarded: iOS Safari Private Browsing (and some strict
 * privacy settings) throw on setItem. We fall back to an in-memory store so the app
 * never crashes when storage is unavailable.
 */

import { api } from "@/lib/api";

const KEY = "chc_attribution";
const SESSION_KEY = "chc_session_id";
const LAST_ARTICLE_KEY = "chc_last_article";

// In-memory fallback when sessionStorage is unavailable (iOS private, strict mode, etc.)
const memStore = {};

function safeGet(key) {
  try {
    if (typeof sessionStorage !== "undefined") {
      const v = sessionStorage.getItem(key);
      if (v !== null) return v;
    }
  } catch (_) {
    /* sessionStorage blocked */
  }
  return memStore[key] ?? null;
}

function safeSet(key, value) {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(key, value);
    }
  } catch (_) {
    /* fall back to memory */
  }
  memStore[key] = value;
}

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getSessionId() {
  let id = safeGet(SESSION_KEY);
  if (!id) {
    id = uuid();
    safeSet(SESSION_KEY, id);
  }
  return id;
}

export function bootstrapAttribution() {
  const existing = safeGet(KEY);
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch (_) {
      /* corrupted — fall through and rewrite */
    }
  }

  let attribution = {
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
    referrer: "",
    landing_page: "",
  };
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    attribution = {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_content: params.get("utm_content") || "",
      utm_term: params.get("utm_term") || "",
      referrer: (typeof document !== "undefined" && document.referrer) || "",
      landing_page: url.pathname + url.search,
    };
  } catch (_) {
    /* URL parsing failed — keep empty attribution */
  }

  safeSet(KEY, JSON.stringify(attribution));
  getSessionId();
  return attribution;
}

export function getAttribution() {
  try {
    const raw = safeGet(KEY);
    return raw ? JSON.parse(raw) : bootstrapAttribution();
  } catch (_) {
    return bootstrapAttribution();
  }
}

export function rememberArticle(slug) {
  if (slug) safeSet(LAST_ARTICLE_KEY, slug);
}

export function getLastArticle() {
  return safeGet(LAST_ARTICLE_KEY) || "";
}

export async function trackEvent(event_type, extras = {}) {
  try {
    const attribution = getAttribution();
    await api.post("/analytics/event", {
      event_type,
      session_id: getSessionId(),
      ...attribution,
      ...extras,
    });
  } catch (_) {
    /* analytics should never break UX */
  }
}
